import {
  all,
  call,
  put,
  takeLatest,
  takeEvery,
  select,
} from 'redux-saga/effects';
import qs from 'qs';
import { request } from '../../common/util/RestClient';
import {
  fetchAllTickets,
  fetchTicketsByEntity,
  fetchTicketsSummary,
  fetchTicketById,
  fetchTicketUpdatesAndComments,
  fetchTicketFiles,
  loadMoreTicketsByEntity,
} from '../routines/ticketRoutines';
import envConfig from '../../config';
import {
  TICKET_ACTIVITY_TYPE,
  TICKET_DEFAULT_PAGE,
  TICKET_DEFAULT_PAGE_SIZE,
} from '../../common/constants/TicketConstants';
import { GLOBAL_DEFAULT_PAGE_SIZE } from '../../common/constants/CommonConstants';
import { convertFilterToVfl } from '../../common/util/velarisFilterLanguageUtils';

export function getTicketsApiHelper(payload = {}) {
  const { page, pageSize, query, filters } = payload || {};
  const params = {
    page: page || 1,
    pageSize: pageSize || GLOBAL_DEFAULT_PAGE_SIZE,
  };
  if (filters && filters.length > 0) {
    params.filters = convertFilterToVfl(filters);
  }

  if (query && query.length > 0) {
    params.query = query;
  }

  const url = `${envConfig.APIG_URL}/ticketing/tickets?${qs.stringify(params)}`;
  return request(url);
}

export function getFetchEntityByIdHelper(payload = {}) {
  const { page, pageSize, query, filters, level, entityId } = payload || {};
  const params = {
    page: page || TICKET_DEFAULT_PAGE,
    pageSize: pageSize || TICKET_DEFAULT_PAGE_SIZE,
    level,
    entityId,
  };
  if (filters && filters.length > 0) {
    params.filters = convertFilterToVfl(filters);
  }

  if (query && query.length > 0) {
    params.query = query;
  }

  const url = `${envConfig.APIG_URL}ticketing/tickets?${qs.stringify(params)}`;
  return request(url);
}
export function* fetchTicketSaga({ payload }) {
  const { level, entityId, filters, query, page, pageSize } = payload;
  try {
    const convertedFilters = convertFilterToVfl(filters);
    const params = {};
    if (convertedFilters) params.filters = convertedFilters;

    if (query) params.query = query;

    if (page) params.page = page;

    if (pageSize) params.pageSize = pageSize;

    let URL = `${envConfig.APIG_URL}ticketing/tickets?level=${level}&entityId=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }
    const response = yield call(request, URL);
    yield put(fetchTicketsByEntity.success(response));
  } catch (error) {
    yield put(fetchTicketsByEntity.failure({ error }));
  } finally {
    yield put(fetchTicketsByEntity.fulfill());
  }
}

export function* fetchTicketByIdSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}ticketing/tickets/${payload.ticketId}?origin=${payload.origin}`;
    const response = yield call(request, URL);
    const files = [];
    const ticketDetails = response?.data;
    if (ticketDetails?.attachments) {
      response.attachments = ticketDetails.attachments?.map((file) => {
        files.push(file?.fileId);
        return { ...file, loading: true };
      });
      if (files.length > 0) {
        const filePayload = {
          ids: files,
        };
        yield put(fetchTicketFiles.trigger(filePayload));
      }
    }
    yield put(fetchTicketById.success(response));
  } catch (error) {
    yield put(fetchTicketById.failure({ error }));
  } finally {
    yield put(fetchTicketById.fulfill());
  }
}

export function* fetchAllTicketSaga({ payload }) {
  const { currentPayload: prevPayload } = yield select(
    (state) => state.tickets
  );

  const payloadToUse = payload?.usePrevPayload ? prevPayload : payload;

  try {
    const response = yield call(getTicketsApiHelper, payloadToUse);
    yield put(
      fetchAllTickets.success({
        response: response.data,
        currentPayload: payloadToUse,
      })
    );
  } catch (error) {
    yield put(fetchAllTickets.failure({ error }));
  } finally {
    yield put(fetchAllTickets.fulfill());
  }
}

export function* fetchTikcetsSummarySaga({ payload }) {
  try {
    const days = payload.days ? `&days=${payload.days}` : '';
    const URL = `${envConfig.APIG_URL}ticketing/tickets/summary?level=${payload.level}&entityId=${payload.entityId}${days}`;
    const response = yield call(request, URL);
    yield put(fetchTicketsSummary.success(response));
  } catch (error) {
    yield put(fetchTicketsSummary.failure({ error }));
  } finally {
    yield put(fetchTicketsSummary.fulfill());
  }
}

export function* fetchUpdatesAndComments({ payload }) {
  try {
    const { ticketId, origin } = payload;
    const URL = `${envConfig.APIG_URL}ticketing/tickets/activity?ticketId=${ticketId}&origin=${origin}`;
    const response = yield call(request, URL);
    const ticketActivities = [...response?.data?.updatesAndComments];
    if (ticketActivities.length > 0) {
      const files = [];
      ticketActivities.map((activity) => {
        if (
          activity?.type === TICKET_ACTIVITY_TYPE.REPLAY &&
          activity?.data?.attachments
        ) {
          activity.data.attachments = activity.data.attachments?.map((file) => {
            files.push(file?.fileId);
            return { ...file, loading: true };
          });
        }
        return true;
      });
      if (files.length > 0) {
        const filePayload = {
          ids: files,
        };
        yield put(fetchTicketFiles.trigger(filePayload));
      }
    }
    const dataPayload = {
      data: ticketActivities,
    };
    yield put(fetchTicketUpdatesAndComments.success(dataPayload));
  } catch (error) {
    yield put(fetchTicketUpdatesAndComments.failure({ error }));
  } finally {
    yield put(fetchTicketUpdatesAndComments.fulfill());
  }
}

export function* fetchTicketAllFiles({ payload }) {
  try {
    const { ids } = payload;
    const URL = `${envConfig.APIG_URL}core/files/get-by-metadata-id`;
    const config = {
      method: 'POST',
      payload: {
        ids,
      },
    };
    const response = yield call(request, URL, config);
    const dataPayload = {
      data: response?.data,
    };
    yield put(fetchTicketFiles.success(dataPayload));
  } catch (error) {
    yield put(fetchTicketFiles.failure({ error }));
  } finally {
    yield put(fetchTicketFiles.fulfill());
  }
}

export function* loadMoreTicketsByEntitySaga({ payload }) {
  try {
    const response = yield call(getFetchEntityByIdHelper, payload);
    yield put(loadMoreTicketsByEntity.success(response));
  } catch (error) {
    yield put(loadMoreTicketsByEntity.failure({ error }));
  } finally {
    yield put(loadMoreTicketsByEntity.fulfill());
  }
}
export default function* root() {
  yield all([
    takeLatest(fetchTicketsByEntity.TRIGGER, fetchTicketSaga),
    takeLatest(fetchTicketsSummary.TRIGGER, fetchTikcetsSummarySaga),
    takeLatest(fetchAllTickets.TRIGGER, fetchAllTicketSaga),
    takeLatest(fetchTicketById.TRIGGER, fetchTicketByIdSaga),
    takeLatest(fetchTicketUpdatesAndComments.TRIGGER, fetchUpdatesAndComments),
    takeEvery(fetchTicketFiles.TRIGGER, fetchTicketAllFiles),
    takeLatest(loadMoreTicketsByEntity.TRIGGER, loadMoreTicketsByEntitySaga),
  ]);
}
